/**
 * Task Force brand and loading states (17 Oct 2026: the dubizzle flame): the flame mark and the dubizzle group
 * wordmark (vectors traced from the brand files, see logo.ts), the tab icon, the start-up screen with the 3D flame,
 * and skeletons shaped like the list, board, sidebar and task view.
 */
import { useEffect, useState } from 'react';
import { BRAND_RED, FLAME, WORDMARK } from './logo';
import flame3d from '../assets/brand/flame-3d.webp';

const [, , FW, FH] = FLAME.viewBox.split(' ').map(Number) as [number, number, number, number];

/** The flame mark. Its dark ball takes the text colour, so it turns light on a dark background. */
export function TfMark({ size = 24, title }: { size?: number; title?: string }) {
  return (
    <svg
      width={(size * FW!) / FH!}
      height={size}
      viewBox={FLAME.viewBox}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      className="tfmark"
    >
      <path fill={BRAND_RED} fillRule="evenodd" d={FLAME.red} />
      <path className="tfmark-ink" fillRule="evenodd" d={FLAME.ink} />
    </svg>
  );
}

/** The dubizzle group wordmark; dark letters take the text colour. */
export function Wordmark({ height = 40, title = 'dubizzle group' }: { height?: number; title?: string }) {
  const [, , w, h] = WORDMARK.viewBox.split(' ').map(Number) as [number, number, number, number];
  return (
    <svg
      width={(height * w) / h}
      height={height}
      viewBox={WORDMARK.viewBox}
      role="img"
      aria-label={title}
      className="tfwordmark"
    >
      <path fill={BRAND_RED} fillRule="evenodd" d={WORDMARK.red} />
      <path className="tfmark-ink" fillRule="evenodd" d={WORDMARK.ink} />
    </svg>
  );
}

/** Mark and name side by side. */
export function TfLogo({ size = 20 }: { size?: number }) {
  return (
    <span className="tflogo-full">
      <TfMark size={size} />
      <span>Task Force</span>
    </span>
  );
}

/** The flame as the browser tab icon (and the tab title) while Task Force is open; the app's own come back on leave. */
const MARK_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${FLAME.viewBox}"><path fill="${BRAND_RED}" fill-rule="evenodd" d="${FLAME.red}"/><path fill="#1b1c1f" fill-rule="evenodd" d="${FLAME.ink}"/></svg>`,
);
export function useTaskforceTab() {
  useEffect(() => {
    const links = [...document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')];
    const before = links.map((l) => l.href);
    const title = document.title;
    links.forEach((l) => (l.href = `data:image/svg+xml,${MARK_SVG}`));
    document.title = 'Task Force';
    return () => {
      links.forEach((l, i) => (l.href = before[i]!));
      document.title = title;
    };
  }, []);
}

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * The start-up screen while Task Force loads for the first time: the dubizzle flame in a soft spotlight (20 Oct 2026:
 * no more mascot). It shows only after a moment, so a quick load never flashes it.
 */
export function Splash() {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setShown(true), 350);
    return () => clearTimeout(id);
  }, []);
  return (
    <div className={'tfsplash' + (shown ? ' on' : '')} role="status" aria-label="Loading Task Force">
      {shown && (
        <>
          <div className="tfsplash-stage" aria-hidden="true">
            <img className={reducedMotion() ? '' : 'float'} src={flame3d} alt="" />
          </div>
          <div className="tfsplash-name">
            <TfMark size={28} /> Task Force
          </div>
          <div className="tfsplash-bar">
            <i />
          </div>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ skeletons

export const S = ({
  w,
  h = 12,
  r = 6,
  className,
}: {
  w: number | string;
  h?: number;
  r?: number;
  className?: string;
}) => <span className={'tfsk' + (className ? ` ${className}` : '')} style={{ width: w, height: h, borderRadius: r }} />;

const TITLE_W = ['72%', '58%', '66%', '48%', '61%', '54%', '69%'];

export function ListSkeleton() {
  return (
    <div className="tflistview" aria-busy="true" aria-label="Loading tasks">
      {[4, 3, 2].map((rows, g) => (
        <section key={g} className="tfgroup">
          <div className="tfgroup-h">
            <S w={14} h={14} r={4} />
            <S w={[96, 132, 110][g]!} h={20} r={6} />
            <S w={14} h={12} />
          </div>
          <div className="tftable">
            <div className="tfthead">
              <span className="tfc-name">
                <S w={40} h={10} />
              </span>
              <span>
                <S w={54} h={10} />
              </span>
              <span>
                <S w={54} h={10} />
              </span>
              <span>
                <S w={48} h={10} />
              </span>
              <span>
                <S w={36} h={10} />
              </span>
            </div>
            {Array.from({ length: rows }, (_, i) => (
              <div key={i} className="tftr tfsk-row">
                <span className="tfc-name">
                  <span className="tfcaret" />
                  <S w={14} h={14} r={4} />
                  <S w={TITLE_W[(g * 3 + i) % TITLE_W.length]!} h={13} />
                </span>
                <span>
                  <S w={24} h={24} r={12} />
                </span>
                <span>
                  <S w={52} h={12} />
                </span>
                <span>
                  <S w={60} h={12} />
                </span>
                <span>
                  <S w={80} h={12} />
                </span>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function BoardSkeleton() {
  return (
    <div className="tfboard" aria-busy="true" aria-label="Loading tasks">
      {[3, 2, 4, 1].map((cards, c) => (
        <div key={c} className="tfcol tfsk-col">
          <div className="tfcol-h">
            <S w={[96, 132, 110, 120][c]!} h={20} r={6} />
            <S w={14} h={12} />
          </div>
          <div className="tfcards">
            {Array.from({ length: cards }, (_, i) => (
              <div key={i} className="tfcard tfsk-card">
                <S w="88%" h={13} />
                <S w="64%" h={13} />
                <div className="tfcard-m">
                  <S w={24} h={24} r={12} />
                  <S w={52} h={11} />
                  <S w={44} h={11} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function SidebarSkeleton() {
  return (
    <div className="tfside-sk" aria-hidden="true">
      {[70, 88, 60, 76].map((w, i) => (
        <div key={i} className="tfrow-main">
          <S w={20} h={20} r={5} />
          <S w={`${w}%`} h={12} />
        </div>
      ))}
    </div>
  );
}

export function TaskSkeleton() {
  return (
    <div className="tfm-sk" aria-busy="true" aria-label="Loading task">
      <div className="tfm-top">
        <div className="tfcrumbs">
          <S w={18} h={18} r={5} />
          <S w={120} h={12} />
        </div>
        <div className="tfm-acts">
          <S w={60} h={18} r={4} />
          <S w={24} h={24} r={6} />
        </div>
      </div>
      <div className="tfm-body">
        <div className="tfm-main">
          <S w={150} h={28} r={6} />
          <div className="tfm-sk-title">
            <S w="86%" h={26} r={8} />
            <S w="52%" h={26} r={8} />
          </div>
          <div className="tfprops">
            {Array.from({ length: 10 }, (_, i) => (
              <div key={i} className="tfprop">
                <span className="k">
                  <S w={15} h={15} r={4} />
                  <S w={[60, 70, 90, 110, 80, 95][i % 6]!} h={12} />
                </span>
                <span className="v">
                  <S w={[86, 120, 70, 140, 64, 100][i % 6]!} h={i === 0 ? 24 : 14} r={6} />
                </span>
              </div>
            ))}
          </div>
          <div className="tfdivider" />
          <div className="tfm-sk-lines">
            <S w="94%" h={12} />
            <S w="88%" h={12} />
            <S w="62%" h={12} />
          </div>
        </div>
        <aside className="tfm-side">
          <div className="tfm-side-h">
            <S w={70} h={14} />
          </div>
          <div className="tffeed">
            {[0, 1, 2].map((i) => (
              <div key={i} className="tfcomment">
                <S w={32} h={32} r={16} />
                <div className="tfm-sk-lines">
                  <S w={140} h={12} />
                  <S w={['90%', '70%', '80%'][i]!} h={12} />
                  {i !== 1 && <S w="55%" h={12} />}
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
